import { Jsona } from "jsona";
import Product, { type IProduct } from "./Product";
import CustomerWishlist, { type ICustomerWishlist } from "./CustomerWishlist";
import axios, { type AxiosResponse } from "axios";

export interface ICustomerWishlistItem {
  id: string | null;
  wishlist?: ICustomerWishlist;
  product?: IProduct;
  qty: number;
}

export default class CustomerWishlistItem implements ICustomerWishlistItem {
  public type: string = "customer-wishlist-items";
  public relationshipNames: string[] = ["wishlist", "product"];

  public id: string | null = null;
  public wishlist?: CustomerWishlist;
  public product?: Product;
  public qty: number = 0;

  public static readonly commonFields: Record<string, string> = {
    "customer-wishlist-items": "qty,wishlist,product",
    "customer-wishlists": "name",
    products: "name",
  };

  public constructor(data?: ICustomerWishlistItem) {
    if (data) {
      this.setData(data);
    }
  }

  public static fromArray(items: ICustomerWishlistItem[]) {
    return items.map((item) => new CustomerWishlistItem(item));
  }

  public static buildCommonUrl(
    path: string,
    productIds: string[] = [],
    fields: Record<string, string> = CustomerWishlistItem.commonFields,
  ): string {
    const fixedFields = Object.keys(fields).reduce(
      (c, f) => {
        if (fields[f]) {
          c[`fields[${f}]`] = fields[f];
        }
        return c;
      },
      {} as Record<string, string>,
    );
    const fieldsQuery = new URLSearchParams(fixedFields).toString();
    const include = "include=wishlist,product";
    let filters = "";
    if (productIds.length > 0) {
      productIds.forEach(
        (pId) => (filters += (filters ? "&" : "") + `filter[product][]=${pId}`),
      );
    }
    return path + "?" + fieldsQuery + "&" + include + "&" + filters;
  }

  private setData({ id, wishlist, product, qty }: ICustomerWishlistItem) {
    this.id = id || null;
    this.wishlist = wishlist ? new CustomerWishlist(wishlist) : undefined;
    this.product = product ? new Product(product) : undefined;
    this.qty = Number.isNaN(qty) ? 0 : qty * 1;
  }

  public async save(): Promise<CustomerWishlistItem> {
    if (!this.product?.id) {
      throw new Error(
        "No se puede guardar un artículo de la lista de deseos si no existe el id de producto.",
      );
    }
    const jsona = new Jsona();
    this.qty = Math.max(this.qty, 1);
    const data = jsona.serialize({ stuff: this, includeNames: [] });
    const url = CustomerWishlistItem.buildCommonUrl(
      "/shop-v1/customer-wishlist-items",
      [this.product.id],
    );
    let res: AxiosResponse;
    if (this.id) {
      res = await axios.patch(url, data);
    } else {
      delete (data.data as { id?: string | null }).id;
      res = await axios.post(url, data);
    }
    if (res.status != 200 && res.status != 201) {
      throw res;
    }
    const newData = jsona.deserialize(res.data);
    this.setData(newData as ICustomerWishlistItem);
    return this;
  }

  public async delete(): Promise<CustomerWishlistItem> {
    await axios.delete(`/shop-v1/customer-wishlist-items/${this.id}`);
    return this;
  }

  public static async getItemsByUserAndProduct(
    customerId: string,
    productIds: string[],
  ): Promise<CustomerWishlistItem[]> {
    const path = `/shop-v1/customers/${customerId}/wishlist-items`;
    const res = await axios.get(
      CustomerWishlistItem.buildCommonUrl(path, productIds),
    );
    const jsona = new Jsona();
    if (res.status != 200) {
      throw res;
    }
    const itemsData = jsona.deserialize(res.data);
    return CustomerWishlistItem.fromArray(itemsData as ICustomerWishlistItem[]);
  }
}
